/**
 * MyCopro - Service Firebase Cloud Messaging (FCM)
 * Gestion unifiée des push notifications web et Android (Capacitor)
 * 
 * ARCHITECTURE:
 * - Web (navigateur) → SDK Firebase Messaging + Service Worker
 * - Android (Capacitor) → Plugin @capacitor/push-notifications (natif)
 * 
 * RESPONSABILITÉS:
 * - Détecter la plateforme (web vs native)
 * - Demander la permission de notification
 * - Récupérer le FCM token
 * - Sauvegarder / supprimer le token dans Firestore (sous-collection members/{uid}/fcmTokens)
 * - Écouter les messages reçus au premier plan (foreground)
 * - S'abonner / se désabonner aux topics de résidence
 * 
 * ✅ CORRECTION: Race condition SW → attendre navigator.serviceWorker.ready
 *    avant getToken() pour éviter "no active Service Worker"
 * 
 * src/services/firebase/messaging.ts
 */

import { getToken, onMessage, Messaging } from 'firebase/messaging';
import {
  doc,
  setDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db, getMessagingInstance } from '@/config/firebase';
import { Capacitor } from '@capacitor/core';
import { logger } from '@/utils/logger';

// ============================================================================
// TYPES
// ============================================================================

/** Plateforme détectée pour le push */
export type PushPlatform = 'web' | 'android' | 'ios' | 'unknown';

/** Callback quand un message push arrive au premier plan */
export interface ForegroundMessage {
  title: string;
  body: string;
  data?: Record<string, string>;
}

export type OnForegroundMessageCallback = (message: ForegroundMessage) => void;

/** Callback quand l'utilisateur tape sur une notification (navigation vers un lien) */
export type OnNotificationTapCallback = (link: string) => void;

// ============================================================================
// CONSTANTES
// ============================================================================

/** Clé VAPID pour les push web (générée dans Firebase Console → Cloud Messaging) */
const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY || '';

// ============================================================================
// DÉTECTION DE PLATEFORME
// ============================================================================

/**
 * Détecte la plateforme courante.
 * Capacitor.isNativePlatform() retourne true sur Android/iOS natif.
 */
export const getPushPlatform = (): PushPlatform => {
  if (Capacitor.isNativePlatform()) {
    const platform = Capacitor.getPlatform();
    if (platform === 'android') return 'android';
    if (platform === 'ios') return 'ios';
    return 'unknown';
  }
  return 'web';
};

// ============================================================================
// GESTION DU TOKEN FCM — FIRESTORE
// ============================================================================

/**
 * Sauvegarde un FCM token dans Firestore.
 * Chemin: members/{userId}/fcmTokens/{tokenHash}
 * Le tokenHash est un hash simple du token pour éviter les doublons.
 */
export const saveFCMToken = async (
  userId: string,
  token: string,
  platform: PushPlatform,
  residenceId?: string
): Promise<void> => {
  try {
    // Utiliser un hash du token comme ID de document pour éviter les doublons
    const tokenId = await hashToken(token);
    const tokenRef = doc(db, `members/${userId}/fcmTokens`, tokenId);

    await setDoc(tokenRef, {
      token,
      platform,
      residenceId: residenceId || null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    }, { merge: true }); // merge: true → met à jour updatedAt si le token existe déjà

    logger.fcm('Token sauvegardé dans Firestore', { platform, tokenId: tokenId.substring(0, 8) + '...' });
  } catch (error) {
    console.error('[FCM] Erreur sauvegarde token:', error);
    throw error;
  }
};

/**
 * Supprime tous les FCM tokens d'un utilisateur (appelé au logout).
 * Supprime uniquement les tokens du device courant (même plateforme).
 */
export const removeFCMTokens = async (
  userId: string,
  platform: PushPlatform
): Promise<void> => {
  try {
    const tokensRef = collection(db, `members/${userId}/fcmTokens`);
    const q = query(tokensRef, where('platform', '==', platform));
    const snapshot = await getDocs(q);

    const deletePromises = snapshot.docs.map((docSnap) =>
      deleteDoc(doc(db, `members/${userId}/fcmTokens`, docSnap.id))
    );

    await Promise.all(deletePromises);
    logger.fcm(`${snapshot.size} token(s) supprimé(s) pour plateforme ${platform}`);
  } catch (error) {
    console.error('[FCM] Erreur suppression tokens:', error);
  }
};

/**
 * Supprime un token spécifique (appelé quand FCM signale un token invalide).
 */
export const removeSpecificFCMToken = async (
  userId: string,
  token: string
): Promise<void> => {
  try {
    const tokenId = await hashToken(token);
    const tokenRef = doc(db, `members/${userId}/fcmTokens`, tokenId);
    await deleteDoc(tokenRef);
    logger.fcm('Token spécifique supprimé');
  } catch (error) {
    console.error('[FCM] Erreur suppression token spécifique:', error);
  }
};

// ============================================================================
// ENREGISTREMENT — WEB (SDK Firebase Messaging)
// ============================================================================

/**
 * Demande la permission et récupère le FCM token sur navigateur web.
 * Nécessite un Service Worker enregistré (firebase-messaging-sw.js).
 * Retourne le token ou null si refusé/non supporté.
 */
export const registerWebPush = async (): Promise<string | null> => {
  try {
    // Vérifier que la clé VAPID est configurée
    if (!VAPID_KEY) {
      logger.fcm('VITE_FIREBASE_VAPID_KEY non configurée dans .env');
      return null;
    }

    // Récupérer l'instance messaging (vérifie le support)
    const messaging = await getMessagingInstance();
    if (!messaging) {
      logger.fcm('Messaging non supporté sur ce navigateur');
      return null;
    }

    // Demander la permission de notification au navigateur
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      logger.fcm('Permission notifications refusée par l\'utilisateur');
      return null;
    }

    // Obtenir la registration du firebase SW directement (pas navigator.serviceWorker.ready
    // qui peut retourner un autre SW déjà actif sur la même origine).
    const swUrl = '/firebase-messaging-sw.js';
    let registration: ServiceWorkerRegistration;

    // Réutiliser la registration existante si déjà enregistrée
    const existing = await navigator.serviceWorker.getRegistration(swUrl);
    if (existing) {
      registration = existing;
      logger.fcm('SW existant réutilisé, scope:', registration.scope);
    } else {
      registration = await navigator.serviceWorker.register(swUrl);
      logger.fcm('SW enregistré, scope:', registration.scope);
    }

    // Attendre que le SW soit dans l'état "active"
    if (registration.installing) {
      logger.fcm('SW en cours d\'installation, attente activation…');
      await new Promise<void>((resolve) => {
        const sw = registration.installing!;
        sw.addEventListener('statechange', function onStateChange() {
          if (sw.state === 'activated' || sw.state === 'redundant') {
            sw.removeEventListener('statechange', onStateChange);
            resolve();
          }
        });
      });
    } else if (!registration.active) {
      // Fallback : attendre via ready si la registration est dans un état inattendu
      logger.fcm('SW en attente (fallback navigator.serviceWorker.ready)…');
      await navigator.serviceWorker.ready;
    }

    logger.fcm('SW actif:', registration.active?.scriptURL);

    // Diagnostiquer l'état de la subscription push existante
    const existingSub = await registration.pushManager.getSubscription();
    logger.fcm('Subscription push existante:', existingSub
      ? existingSub.endpoint.substring(0, 60) + '…'
      : 'aucune');

    // Récupérer le token FCM
    try {
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        logger.fcm('Token obtenu:', token.substring(0, 20) + '...');
        return token;
      }
      logger.fcm('Aucun token retourné par getToken()');
      return null;

    } catch (tokenError) {
      const err = tokenError as Error;
      logger.fcm(`getToken() échec: ${err.name} — ${err.message}`);

      // AbortError "push service error" = subscription périmée ou clé VAPID changée
      // → nettoyer la subscription existante et réessayer une fois
      if (err.name === 'AbortError') {
        logger.fcm('Nettoyage de la subscription et retry…');
        if (existingSub) {
          await existingSub.unsubscribe();
          logger.fcm('Ancienne subscription supprimée');
        }

        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: registration,
        });

        if (token) {
          logger.fcm('Token obtenu après retry:', token.substring(0, 20) + '...');
          return token;
        }
        logger.fcm('Retry échoué — push non disponible sur cette session');
        return null;
      }

      throw tokenError;
    }

  } catch (error) {
    const err = error as Error;
    // AbortError = push service indisponible (réseau, clé VAPID, état Chrome)
    // Ce n'est pas une erreur de code → log diagnostic uniquement
    if (err.name === 'AbortError') {
      logger.fcm(`Push service indisponible: ${err.message}`);
    } else {
      console.error('[FCM Web] Erreur enregistrement:', error);
    }
    return null;
  }
};

/**
 * Écoute les messages push reçus au premier plan (foreground) sur web.
 * Quand l'app est ouverte, FCM ne montre pas de notification système,
 * donc on affiche un toast in-app à la place.
 * Retourne la fonction de désabonnement.
 */
export const listenForegroundMessagesWeb = (
  callback: OnForegroundMessageCallback
): (() => void) | null => {
  // On doit récupérer l'instance de manière synchrone ici
  // car onMessage ne supporte pas les promesses.
  // On utilise getMessagingInstance() de manière asynchrone puis on set up le listener.
  let unsubscribe: (() => void) | null = null;

  getMessagingInstance().then((messaging) => {
    if (!messaging) return;

    unsubscribe = onMessage(messaging, (payload) => {
      logger.fcm('Message foreground reçu:', payload);

      const title = payload.notification?.title || payload.data?.title || 'Notification';
      const body = payload.notification?.body || payload.data?.message || '';
      const data = payload.data as Record<string, string> | undefined;

      callback({ title, body, data });
    });
  });

  // Retourner un wrapper de désabonnement
  return () => {
    if (unsubscribe) unsubscribe();
  };
};

// ============================================================================
// ENREGISTREMENT — ANDROID (Capacitor Plugin)
// ============================================================================

/**
 * Demande la permission et récupère le FCM token sur Android via Capacitor.
 * Utilise @capacitor/push-notifications.
 * Retourne le token ou null si refusé.
 */
export const registerAndroidPush = async (): Promise<string | null> => {
  try {
    // Import dynamique pour éviter les erreurs sur web
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Vérifier les permissions
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      logger.fcm('Android - Permission notifications refusée');
      return null;
    }

    // Enregistrer pour recevoir les notifications
    await PushNotifications.register();

    // Récupérer le token via une promesse
    return new Promise<string | null>((resolve) => {
      // Timeout de sécurité (10 secondes)
      const timeout = setTimeout(() => {
        logger.fcm('Android - Timeout en attendant le token');
        resolve(null);
      }, 10000);

      PushNotifications.addListener('registration', (tokenResult) => {
        clearTimeout(timeout);
        logger.fcm('Android - Token obtenu:', tokenResult.value.substring(0, 20) + '...');
        resolve(tokenResult.value);
      });

      PushNotifications.addListener('registrationError', (error) => {
        clearTimeout(timeout);
        console.error('[FCM Android] Erreur enregistrement:', error);
        resolve(null);
      });
    });
  } catch (error) {
    console.error('[FCM Android] Erreur:', error);
    return null;
  }
};

/**
 * Écoute les messages push reçus au premier plan sur Android.
 * Retourne la fonction de cleanup.
 */
export const listenForegroundMessagesAndroid = async (
  callback: OnForegroundMessageCallback,
  onNotificationTap?: OnNotificationTapCallback
): Promise<(() => void) | null> => {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Notification reçue au premier plan
    const foregroundListener = await PushNotifications.addListener(
      'pushNotificationReceived',
      (notification) => {
        logger.fcm('Android - Push foreground:', notification);
        callback({
          title: notification.title || 'Notification',
          body: notification.body || '',
          data: notification.data as Record<string, string> | undefined,
        });
      }
    );

    // Notification cliquée (l'utilisateur tape sur la notif)
    const actionListener = await PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action) => {
        logger.fcm('Android - Push action:', action);
        // Le lien de redirection est dans action.notification.data.link
        const link = action.notification.data?.link;
        if (link) {
          if (onNotificationTap) {
            // Navigation côté client (React Router) — évite un rechargement complet
            // de la WebView, dont le fallback SPA sur un chemin direct n'est pas
            // fiable sur iOS (contrairement à Android).
            onNotificationTap(link);
          } else {
            window.location.href = link;
          }
        }
      }
    );

    return () => {
      foregroundListener.remove();
      actionListener.remove();
    };
  } catch (error) {
    console.error('[FCM Android] Erreur listener foreground:', error);
    return null;
  }
};

// ============================================================================
// ENREGISTREMENT — iOS (Capacitor Plugin + pont Firebase natif)
// ============================================================================

/**
 * Demande la permission et récupère le token FCM sur iOS.
 * @capacitor/push-notifications gère la permission OS + l'enregistrement APNs
 * (le device token natif est transmis à Firebase côté Swift, voir AppDelegate.swift).
 * Le token FCM lui-même est récupéré via le plugin natif maison FirebaseTokenBridge,
 * car @capacitor/push-notifications ne retourne que le token APNs brut sur iOS.
 */
export const registerIOSPush = async (): Promise<string | null> => {
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }
    if (permStatus.receive !== 'granted') {
      logger.fcm('iOS - Permission notifications refusée');
      return null;
    }

    await PushNotifications.register();

    return await getIOSFCMTokenWithRetry();
  } catch (error) {
    console.error('[FCM iOS] Erreur enregistrement:', error);
    return null;
  }
};

/**
 * Récupère le token FCM via FirebaseTokenBridge, avec quelques tentatives:
 * la conversion APNs → FCM côté SDK natif n'est pas toujours instantanée
 * juste après PushNotifications.register().
 */
const getIOSFCMTokenWithRetry = async (
  retries = 5,
  delayMs = 1000
): Promise<string | null> => {
  const { FirebaseTokenBridge } = await import('@/native/FirebaseTokenBridge');

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const { token } = await FirebaseTokenBridge.getToken();
      if (token) {
        logger.fcm('iOS - Token FCM obtenu:', token.substring(0, 20) + '...');
        return token;
      }
    } catch (error) {
      logger.fcm(`iOS - Tentative ${attempt}/${retries} échouée: ${(error as Error).message}`);
    }
    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  logger.fcm('iOS - Impossible d\'obtenir le token FCM après plusieurs tentatives');
  return null;
};

/**
 * Écoute les messages push reçus au premier plan sur iOS.
 * @capacitor/push-notifications gère les listeners 'pushNotificationReceived' /
 * 'pushNotificationActionPerformed' de façon générique cross-plateforme —
 * on réutilise donc listenForegroundMessagesAndroid tel quel (aucun code
 * spécifique Android dedans malgré le nom).
 */
export const listenForegroundMessagesIOS = listenForegroundMessagesAndroid;

// ============================================================================
// API UNIFIÉE
// ============================================================================

/**
 * Enregistre les push notifications pour l'utilisateur courant.
 * Détecte automatiquement la plateforme et utilise le bon mécanisme.
 * Sauvegarde le token dans Firestore.
 * 
 * @param userId - UID de l'utilisateur Firebase
 * @param residenceId - ID de la résidence courante (optionnel)
 * @returns Le token FCM obtenu, ou null si échec
 */
export const registerPushNotifications = async (
  userId: string,
  residenceId?: string
): Promise<string | null> => {
  const platform = getPushPlatform();
  let token: string | null = null;

  logger.fcm(`Enregistrement push pour plateforme: ${platform}`);

  switch (platform) {
    case 'web':
      token = await registerWebPush();
      break;
    case 'android':
      token = await registerAndroidPush();
      break;
    case 'ios':
      token = await registerIOSPush();
      break;
    default:
      logger.fcm('Plateforme inconnue, push non disponible');
      return null;
  }

  // Sauvegarder le token dans Firestore si obtenu
  if (token) {
    await saveFCMToken(userId, token, platform, residenceId);
  }

  return token;
};

/**
 * Désinscrit les push notifications pour l'utilisateur courant.
 * Supprime les tokens de la plateforme courante dans Firestore.
 */
export const unregisterPushNotifications = async (userId: string): Promise<void> => {
  const platform = getPushPlatform();
  await removeFCMTokens(userId, platform);
  logger.fcm('Désinscription push terminée');
};

/**
 * Initialise l'écoute des messages foreground selon la plateforme.
 * Retourne la fonction de cleanup.
 */
export const initForegroundListener = async (
  callback: OnForegroundMessageCallback,
  onNotificationTap?: OnNotificationTapCallback
): Promise<(() => void) | null> => {
  const platform = getPushPlatform();

  switch (platform) {
    case 'web':
      return listenForegroundMessagesWeb(callback);
    case 'android':
      return await listenForegroundMessagesAndroid(callback, onNotificationTap);
    case 'ios':
      return await listenForegroundMessagesIOS(callback, onNotificationTap);
    default:
      return null;
  }
};

// ============================================================================
// UTILITAIRES
// ============================================================================

/**
 * Hash simple d'un token pour l'utiliser comme ID de document Firestore.
 * Utilise SubtleCrypto si disponible, sinon un hash basique.
 */
const hashToken = async (token: string): Promise<string> => {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const encoder = new TextEncoder();
      const data = encoder.encode(token);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    // Fallback si SubtleCrypto non disponible
  }

  // Hash simple (djb2) en fallback
  let hash = 5381;
  for (let i = 0; i < token.length; i++) {
    hash = ((hash << 5) + hash) + token.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  return 'fcm_' + Math.abs(hash).toString(16);
};
